import {
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-use';
import { twMerge } from 'tailwind-merge';
import cn from 'classnames';
import { useOutsideClick } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import { authSelector, isLoggedSelector } from 'src/selectors/auth';
import { isIABMSelector } from 'src/selectors/app';
import {
    Button,
    Checkbox,
    Element,
    Icon,
    Image,
    RouterLink,
    Title,
} from 'src/components';
import ZaloLoginButton from 'src/modules/shared/components/buttons/ZaloLoginButton';
import DropdownSetting from './dropdown';

function UserProfile() {
    const { auth } = useAuth();
    const location = useLocation();
    const isLogged = useSelector(isLoggedSelector);
    const isIABM = useSelector(isIABMSelector);
    const authState = useSelector(authSelector);
    const [isExpand, setIsExpand] = useState<boolean>(false);
    const btnRef: MutableRefObject<any> = useRef<RefObject<any>>(null);
    const [checked, setChecked] = useState<boolean>(false);

    useEffect(() => {
        if (!isLogged) {
            auth();
        }
    }, [isLogged]);

    useEffect(() => {
        setIsExpand(false);
    }, [location]);

    const handleClick = useCallback(
        (evt: React.MouseEvent) => {
            setIsExpand(!isExpand);
        },
        [isExpand],
    );

    useEffect(() => {
        (window as any).authState = authState;
    }, [authState]);

    useOutsideClick([btnRef], (isExpand) => {
        if (!isExpand) {
            setIsExpand(false);
        }
    });

    const renderTriggerButton = useCallback(() => {
        if (!isLogged) {
            return (
                <Icon
                    className="text-[22px] text-[#666]"
                    icon="new-user-light"
                />
            );
        }
        return (
            <>
                <Image
                    className={cn(
                        'w-[32px] h-[32px]',
                        'rounded-full',
                        'align-top overflow-hidden',
                    )}
                    src={authState.avatar}
                ></Image>
            </>
        );
    }, [isLogged, authState]);

    const onChange = useCallback(() => {
        setChecked(!checked);
    }, [checked]);

    const renderDropdownContent = useCallback(() => {
        if (!isLogged) {
            return (
                <Element className={cn('login-dropdown', 'p-[20px]')}>
                    <Title className="font-[800] leading-[1.6]">
                        Tài khoản
                    </Title>
                    <Element
                        className={cn(
                            'font-semibold text-[12px]',
                            'text-[#666]',
                            'my-[12px]',
                        )}
                    >
                        Bạn đồng ý với việc Đăng nhập/Đăng ký từ tài khoản liên
                        kết Zalo
                    </Element>
                    <Checkbox
                        className="flex items-start mb-[10px] [&_a]:underline"
                        classNameLabel="text-[12px] font-[800] leading-[1.5] text-[#2F3133]"
                        classNameCheckbox="p-[5px] rounded-none checked:after:border-inherit"
                        checked={checked}
                        onChange={onChange}
                    >
                        Tôi đã đọc và đồng ý với{' '}
                        <RouterLink to="/staticpages/termsofuse.epi" newTab>
                            Điều khoản sử dụng
                        </RouterLink>{' '}
                        và{' '}
                        <RouterLink to="/staticpages/privacy.epi" newTab>
                            Chính sách bảo mật
                        </RouterLink>
                    </Checkbox>
                    <Element className={checked ? '' : 'disabled'}>
                        <ZaloLoginButton />
                    </Element>
                </Element>
            );
        }
        return <DropdownSetting />;
    }, [isLogged, checked]);

    if (isIABM) {
        return null;
    }

    return (
        <Element className={'user-profile relative mr-[8px]'} domRef={btnRef}>
            <Button
                className={cn(
                    'flex items-center justify-center',
                    'h-[36px] w-[36px]',
                    'bg-[#f2f2f2]',
                    'rounded-full',
                    cn({
                        ['bg-[#DAF1F6]']: isExpand,
                    }),
                )}
                onClick={handleClick}
                title="Cá nhân"
            >
                {renderTriggerButton()}
            </Button>
            <Element
                className={twMerge(
                    'user-setting-dropdown',
                    'absolute right-[-14px] top-[46px]',
                    'px-0',
                    'text-[14px] leading-[1.5] font-normal',
                    'border border-solid border-[#e4e4e4]',
                    'w-[200px] rounded-[5px]',
                    'bg-white',
                    'shadow-[0_0_3px_0_rgba(0,0,0,0.2)]',
                    'invisible',
                    'after:clear-both',
                    'after:w-0 after:h-0',
                    'after:absolute after:bottom-full after:right-[25px]',
                    'after:border-b-[#fff] after:border-solid after:border-[5px]',
                    'before:clear-both',
                    'before:w-0 before:h-0',
                    'before:absolute before:bottom-full before:right-[24px]',
                    'before:border-b-[#e4e4e4] before:border-solid before:border-[6px]',
                    cn({
                        ['visible opacity-100']: isExpand,
                    }),
                )}
            >
                {renderDropdownContent()}
            </Element>
        </Element>
    );
}

export default UserProfile;
